"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  loginUser,
  registerUser,
  changePassword,
  resetPassword,
  resetPasswordWithYearOfBirth,
  registerAndEnrollUser,
} from "../query/auth";
import { useRouter } from "next/navigation";
import { enrollCourse } from "../query/courses";
import type { RegisterCredentials } from "../../../types/auth";
import {
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  isAuthenticated,
  getPendingEnrollCourseId,
  clearPendingEnrollCourseId,
} from "../utils/session";

// Re-exported so existing imports from "useAuth" keep working
export { setAuthToken, getAuthToken, isAuthenticated };

export const useLogin = (onSuccessRedirect?: (token: string) => void) => {
  return useMutation({
    mutationFn: loginUser,
    onSuccess: async (data) => {
      setAuthToken(data.token);
      toast.success("Login successful!");

      const pendingCourseId = getPendingEnrollCourseId();
      if (pendingCourseId) {
        clearPendingEnrollCourseId();
        try {
          await enrollCourse(pendingCourseId, null);
          toast.success("Successfully enrolled in the course!");
          window.location.href = "/dashboard/courses";
        } catch (error) {
          console.error("Failed to auto-enroll in course:", error);
          window.location.href = "/dashboard";
        }
      } else if (onSuccessRedirect) {
        onSuccessRedirect(data.token);
      } else {
        window.location.href = "/dashboard";
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data || "Login failed. Please try again.";
      toast.error(errorMessage);
    },
  });
};

export function useRegister(
  onSuccess?: () => void,
  onError?: (error: any) => void,
) {
  return useMutation({
    mutationFn: registerUser,
    onSuccess: async (data) => {
      if (data?.token) {
        setAuthToken(data.token);

        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      onSuccess?.();
    },
    onError,
  });
}

export function useRegisterAndEnroll(
  onSuccess?: () => void,
  onError?: (error: any) => void,
) {
  return useMutation({
    mutationFn: ({
      userData,
      courseId,
    }: {
      userData: RegisterCredentials;
      courseId: number;
      memberNo?: string;
    }) => registerAndEnrollUser(userData, courseId),
    onSuccess: async (data) => {
      if (data?.token) {
        setAuthToken(data.token);
        // Same cookie-commit guard as useRegister
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      onSuccess?.();
    },
    onError,
  });
}

export const useChangePassword = () => {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success("Password changed successfully!");
      window.location.href = "/dashboard";
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data || "Failed to change password. Please try again.";
      toast.error(errorMessage);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: resetPassword,
    onError: (error: any) => {
      const errorMessage =
        error.response?.data || "Failed to reset password. Please try again.";
      toast.error(errorMessage);
    },
  });
};

export const useResetPasswordWithYearOfBirth = () => {
  return useMutation({
    mutationFn: resetPasswordWithYearOfBirth,
    onSuccess: (data) => {
      return data;
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data || "Failed to reset password. Please try again.";
      toast.error(errorMessage);
      throw error;
    },
  });
};

export const useLogout = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const logout = () => {
    removeAuthToken();
    queryClient.clear();
    toast.success("Logged out successfully");
    router.push("/sign-in");
    router.refresh();
  };
  return { logout };
};
